import React from "react";
import { compose } from "redux";
import { localize } from "redux-i18n";
import { connect } from "react-redux";
import type { PaginationExtendedType } from "@types";
import type {
  EmployeeNotification,
  EmployeeRefType,
  NotificationActionType,
  NotificationsContent,
  NotificationIndividualsRevisionHistoryItem,
} from "@modules/shared/reducer/employee-notifications/types";
import { Button, GSGrid, Pagination, GSRenderers, ConfirmationModal } from "@components";
import actionCreators from "@modules/shared/actionCreator";
import type { State } from "@store/reducers";
import {
  getNotificationsIndividuals,
  getNotificationsIndividualsPagination,
  getIndividualsRevisionHistoryModal,
} from "@modules/shared/selectors/employee-notifications";
import type { EmployeeType } from "@modules/employee/types";
import {
  NOTIFICATION_ACTION_TYPES,
  EMPLOYEE_REF_TYPES,
} from "@modules/shared/constants/notifications";
import { NotificationDeleteModal } from "@modules/shared/components";
import { NotificationActionModal } from "@modules/shared/components";
import { formatDateTime } from "@transformers/date";
import { PAGE_CONTENT_TYPE } from "@modules/employee/components/employee-contacts";

type OwnProps = $Exact<{
  clientId: number,
  employeeId: number,
  employeeName: string,
  employeeRefType: EmployeeRefType,
  contentType: typeof PAGE_CONTENT_TYPE,
  allowEdit?: boolean,
  hideTransfer?: boolean,
  deleteModalTitle: string,
  deleteModalContent: string,
  deleteModalContentPlural: string,
  isMyEmployeesNotifications?: boolean,
  isReportingSection?: boolean,
}>;

type PropsFromState = $Exact<{
  notifications: Array<EmployeeNotification>,
  pagination: PaginationExtendedType,
  individualsRevisionHistoryModal: $Exact<{
    visible: boolean,
    data: NotificationsContent<NotificationIndividualsRevisionHistoryItem>,
  }>,
}>;

type PropsFromDispatch = $Exact<{
  getNotifications: typeof actionCreators.GetNotifications,
  getMyEmployeeNotifications: typeof actionCreators.GetMyEmployeeNotifications,
  getNotificationsTargets: typeof actionCreators.GetNotificationsTargets,
  deleteNotificationList: typeof actionCreators.DeleteNotificationList,
  deleteNotificationTargetList: typeof actionCreators.DeleteNotificationTargetList,
  getNotificationEmployee: typeof actionCreators.GetNotificationEmployee,
  transferNotificationList: typeof actionCreators.TransferNotificationList,
  transferNotificationTargetList: typeof actionCreators.TransferNotificationTargetList,
  copyNotificationList: typeof actionCreators.CopyNotificationList,
  copyNotificationTargetList: typeof actionCreators.CopyNotificationTargetList,
  deleteManagerNotificationList: typeof actionCreators.DeleteManagerNotificationList,
  toggleIndividualsRevisionHistoryModal: typeof actionCreators.ToggleIndividualsRevisionHistoryModal,
  getIndividualsRevisionHistory: typeof actionCreators.GetIndividualsRevisionHistory,
  resetNotificationList: typeof actionCreators.ResetNotificationModalEmployeeList,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

type ComponentState = $Exact<{
  selectedIndexes: Array<number>,
  singleDeletionId: ?number,
  isDeleteModalOpen: boolean,
  actionModal: $Exact<{
    isOpen: boolean,
    actionType: NotificationActionType,
    data: Array<EmployeeNotification>,
  }>,
}>;

const refType = type => {
  return type === EMPLOYEE_REF_TYPES.TARGET
    ? EMPLOYEE_REF_TYPES.TARGET
    : EMPLOYEE_REF_TYPES.RECIPIENT;
};

export class NotificationsIndividuals extends React.Component<Props, ComponentState> {
  state = {
    selectedIndexes: [],
    singleDeletionId: null,
    isDeleteModalOpen: false,
    actionModal: {
      isOpen: false,
      actionType: NOTIFICATION_ACTION_TYPES.TRANSFER,
      data: [],
    },
    showConfirmActionModal: false,
    transferTargetId: null,
    successMessage: "",
  };

  componentDidMount() {
    this.getNotificationsData();
  }

  componentDidUpdate(prevProps: Props) {
    if (
      this.props.employeeRefType &&
      prevProps.employeeRefType &&
      this.props.employeeRefType !== prevProps.employeeRefType
    ) {
      this.getNotificationsData();
    }
  }

  getNotificationsData = () => {
    if (
      (this.props.employeeRefType === EMPLOYEE_REF_TYPES.RECIPIENT ||
        this.props.employeeRefType === EMPLOYEE_REF_TYPES.MANAGER) &&
      !this.props.isMyEmployeesNotifications
    ) {
      this.props.getNotifications({
        clientId: this.props.clientId,
        employeeId: this.props.employeeId,
      });
    } else if (this.props.employeeRefType === EMPLOYEE_REF_TYPES.TARGET) {
      this.props.getNotificationsTargets({
        clientId: this.props.clientId,
        employeeId: this.props.employeeId,
      });
    }
    if (this.props.isMyEmployeesNotifications) {
      this.props.getMyEmployeeNotifications({
        clientId: this.props.clientId,
        employeeId: this.props.employeeId,
      });
    }
  };

  handlePaginationChange = (page: number) => {
    if (
      this.props.employeeRefType === EMPLOYEE_REF_TYPES.RECIPIENT ||
      this.props.employeeRefType === EMPLOYEE_REF_TYPES.MANAGER
    ) {
      this.props.getNotifications({
        clientId: this.props.clientId,
        employeeId: this.props.employeeId,
        page,
      });
    } else if (this.props.employeeRefType === EMPLOYEE_REF_TYPES.TARGET) {
      this.props.getNotificationsTargets({
        clientId: this.props.clientId,
        employeeId: this.props.employeeId,
        page,
      });
    }
  };

  handleEditNotification = (notification: EmployeeNotification) => {
    this.props.getNotificationEmployee({
      clientId: this.props.clientId,
      employeeId: notification[refType(this.props.employeeRefType)].id,
      notificationType: notification.notificationType,
      notificationId: notification.id,
      employeeRefType: refType(this.props.employeeRefType),
      employeePositions: notification.employeePositions,
      notificationDates: {
        activeFrom: notification.activeFrom,
        activeTo: notification.activeTo,
      },
    });
  };

  handleDeleteNotificationList = (idList: Array<number>) => {
    const data = {
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      notificationIdList: idList,
    };
    if (this.props.employeeRefType === EMPLOYEE_REF_TYPES.RECIPIENT) {
      this.props.deleteNotificationList(data);
    } else if (this.props.employeeRefType === EMPLOYEE_REF_TYPES.TARGET) {
      this.props.deleteNotificationTargetList(data);
    } else if (this.props.employeeRefType === EMPLOYEE_REF_TYPES.MANAGER) {
      this.props.deleteManagerNotificationList(data);
    }
  };

  handleTransferNotification = (employee: EmployeeType, payload: Object) => {
    if (
      this.props.employeeRefType === EMPLOYEE_REF_TYPES.RECIPIENT ||
      this.props.employeeRefType === EMPLOYEE_REF_TYPES.MANAGER
    ) {
      this.props.transferNotificationList(payload);
    } else if (this.props.employeeRefType === EMPLOYEE_REF_TYPES.TARGET) {
      this.props.transferNotificationTargetList(payload);
    }
  };

  handleCopyNotification = (employee: EmployeeType, payload: Object) => {
    if (this.props.contentType === PAGE_CONTENT_TYPE.NOTIFICATIONS_TO)
      payload.notificationList.forEach(itm => (itm.employeePositions = []));
    if (
      this.props.employeeRefType === EMPLOYEE_REF_TYPES.RECIPIENT ||
      this.props.employeeRefType === EMPLOYEE_REF_TYPES.MANAGER
    ) {
      this.props.copyNotificationList(payload);
    } else if (this.props.employeeRefType === EMPLOYEE_REF_TYPES.TARGET) {
      this.props.copyNotificationTargetList(payload);
    }
  };
  getIndividualsRevisionHistory = (params: Object) => {
    this.props.getIndividualsRevisionHistory(params);
  };

  get allSelected() {
    return this.state.selectedIndexes.length === this.props.notifications.length;
  }

  deselectAll = () => {
    this.setState({
      selectedIndexes: [],
    });
  };

  selectedAll = () => {
    this.setState({
      selectedIndexes: this.props.notifications.map((item: EmployeeNotification) => item.id),
    });
  };

  toggleSelectAll = () => {
    if (this.allSelected) {
      this.deselectAll();
      return;
    }
    this.selectedAll();
  };

  isSelected = (id: number) => {
    return this.state.selectedIndexes.includes(id);
  };

  deselectOne = (id: number) => {
    this.setState({
      ...this.state,
      selectedIndexes: this.state.selectedIndexes.filter(i => i !== id),
    });
  };

  handleEditNotificationClick = (item: EmployeeNotification) => () => {
    this.handleEditNotification(item);
  };

  showDeleteModal = (singleDeletionId?: number) => () => {
    if (singleDeletionId) {
      this.setState({
        singleDeletionId,
        isDeleteModalOpen: true,
      });
      return;
    }
    this.setState({
      isDeleteModalOpen: true,
    });
  };

  closeDeleteModal = () => {
    this.setState({
      singleDeletionId: null,
      isDeleteModalOpen: false,
    });
  };

  handleDelete = () => {
    if (this.state.singleDeletionId !== null) {
      this.handleDeleteNotificationList([this.state.singleDeletionId]);
      this.deselectOne(this.state.singleDeletionId || 0);
    } else {
      this.handleDeleteNotificationList(this.state.selectedIndexes);
      this.deselectAll();
    }
    this.closeDeleteModal();
  };

  handleTransfer = (items: Array<EmployeeNotification>) => () => {
    this.setState({
      actionModal: {
        isOpen: true,
        actionType: NOTIFICATION_ACTION_TYPES.TRANSFER,
        data: items,
      },
    });
  };

  getIndividualsRevisionHistory = (item: EmployeeNotification) => () => {
    this.props.getIndividualsRevisionHistory({
      clientId: this.props.clientId,
      notificationId: item.id,
    });
  };

  handleCopy = (items: Array<EmployeeNotification>) => () => {
    this.setState({
      actionModal: {
        isOpen: true,
        actionType: NOTIFICATION_ACTION_TYPES.COPY,
        data: items,
      },
    });
  };

  handleCloseActionModal = () => {
    this.setState({
      actionModal: {
        isOpen: false,
        actionType: NOTIFICATION_ACTION_TYPES.TRANSFER,
        data: [],
      },
      showConfirmActionModal: false,
      transferTargetId: null,
    });
  };
  discardActionModal = () => {
    this.handleCloseActionModal();
  };

  handleConfirmSubmitAction = (data: Object) => {
    this.handleSubmitAction();
  };

  showConfirmActionModal = (employee: EmployeeType, successMessage: string) => {
    this.setState({
      ...this.state,
      showConfirmActionModal: true,
      transferTargetId: employee.id,
      successMessage,
    });
  };

  handleSubmitAction = (employee: EmployeeType, successMessage: string) => {
    const payload = {
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      notificationList: this.state.actionModal.data,
      transferTargetId: this.state.transferTargetId,
      successMessage: this.state.successMessage,
    };

    if (this.state.actionModal.actionType === NOTIFICATION_ACTION_TYPES.TRANSFER) {
      this.handleTransferNotification(employee, payload);
    } else if (this.state.actionModal.actionType === NOTIFICATION_ACTION_TYPES.COPY) {
      this.handleCopyNotification(employee, payload);
    }
    this.props.resetNotificationList();
    this.handleCloseActionModal();
    this.deselectAll();
  };

  toggleIndividualsRevisionHistoryModal = () => {
    this.props.toggleIndividualsRevisionHistoryModal();
  };

  get columns() {
    let _col = [
      {
        key: "employeeRef",
        name: this.props.t("NOTIFICATIONS_EMPLOYEE_REF"),
        renderCell: ({ row }: { row: EmployeeNotification }) => {
          return row[this.refType].employeeRefPrimary;
        },
      },
      {
        key: "employeeName",
        name: this.props.t("NOTIFICATIONS_EMPLOYEE_NAME"),
        renderCell: ({ row }: { row: EmployeeNotification }) => row[this.refType].name,
        align: "left",
      },
      {
        key: "notificationType",
        name: this.props.t("NOTIFICATIONS_NOTIFICATION_TYPE"),
        renderCell: ({ row }: { row: EmployeeNotification }) => this.props.t(row.notificationType),
        align: "left",
      },
      {
        key: "dei",
        name: this.props.t("DEI"),
        renderCell: ({ row }: { row: EmployeeNotification }) => {
          if (row.notificationType === "BASIC_NOTIFICATION") {
            return <div className="circle font-weight-bold bg-mint">L</div>;
          }
          return row.notificationType === "STANDARD_NOTIFICATION" ? (
            <div className="circle font-weight-bold bg-coffee">M</div>
          ) : (
            <div className="circle font-weight-bold bg-rose">H</div>
          );
        },
      },
      {
        key: "positionScope",
        name: this.props.t("POSITION_SCOPE"),
        renderCell: ({ row }: { row: EmployeeNotification }) => {
          if (row.employeePositions && row.employeePositions.length) {
            const data = row.employeePositions.map(
              position => position.jobTitle || position.employeeReference || position.id,
            );
            return data.join(", ");
          }
          return this.props.t("ALL_POSITIONS");
        },
        align: "left",
      },
      {
        key: "dateAdded",
        name: this.props.t("NOTIFICATIONS_DATE_ADDED"),
        renderCell: ({ row }: { row: EmployeeNotification }) => formatDateTime(row.createdAt),
        width: "min-content",
      },
      {
        key: "addedBy",
        name: this.props.t("NOTIFICATIONS_ADDED_BY"),
        renderCell: ({ row }: { row: EmployeeNotification }) =>
          GSRenderers.updatedBy({ ...row, t: this.props.t }),
        align: "left",
        width: "min-content",
      },
    ];

    if (this.props.allowEdit) {
      _col.push({
        key: "options",
        name: this.props.t("OPTIONS"),
        contextMenuConfig: (row: EmployeeNotification) => [
          {
            label: this.props.t("EDIT"),
            icon: "edit",
            onClick: this.handleEditNotificationClick(row),
          },
          {
            label: this.props.t("COPY"),
            icon: "content_copy",
            onClick: this.handleCopy([row]),
          },
          {
            label: this.props.t("TRANSFER"),
            icon: "drive_file_move",
            onClick: this.handleTransfer([row]),
          },
          {
            label: this.props.t("HISTORY"),
            icon: "history",
            onClick: this.getIndividualsRevisionHistory(row),
          },
          {
            label: this.props.t("DELETE"),
            icon: "delete",
            disabled: !!row.approval,
            className: "text-color-negative",
            onClick: this.showDeleteModal(row.id),
          },
        ],
      });
    }

    return _col;
  }

  get notificationEmployeesLabels(): Array<string> {
    if (this.state.singleDeletionId) {
      const selectedNotification = this.props.notifications.find(
        item => item.id === this.state.singleDeletionId,
      );
      return selectedNotification
        ? [
            `${selectedNotification[this.refType].firstName} ${
              selectedNotification[this.refType].lastName
            }`,
          ]
        : [""];
    }
    return this.props.notifications
      .filter(item => this.state.selectedIndexes.includes(item.id))
      .map(item => `${item[this.refType].firstName} ${item[this.refType].lastName}`);
  }

  get selectedNotifications(): Array<EmployeeNotification> {
    return this.state.selectedIndexes.map(notiIdx => {
      return this.props.notifications.find(not => not.id == notiIdx);
    });
  }

  get refType() {
    switch (this.props.employeeRefType) {
      case EMPLOYEE_REF_TYPES.TARGET:
        return this.props.employeeRefType;

      case EMPLOYEE_REF_TYPES.RECIPIENT:
        return this.props.employeeRefType;

      default:
        return EMPLOYEE_REF_TYPES.RECIPIENT;
    }
  }

  get revisionHistoryData() {
    return (this.props.individualsRevisionHistoryModal.data || {}).content || [];
  }

  handleOnSelectedRowsChange = (selectedIndexes: number[]) => {
    this.setState({
      selectedIndexes,
    });
  };

  render() {
    const actionType = this.state.actionModal.actionType;
    return (
      <React.Fragment>
        {this.props.notifications && (
          <GSGrid
            selectedRows={this.state.selectedIndexes}
            columns={this.columns}
            rows={this.props.notifications}
            onSelectedRowsChange={this.props.allowEdit ? this.handleOnSelectedRowsChange : null}
          />
        )}
        {!!this.state.selectedIndexes.length && (
          <div className="d-flex flex-row actions align-items-center p-3 w-100">
            <div className="text-warning mr-3">
              {this.props.t("NUMBER_RECORDS_SELECTED", {
                number: this.state.selectedIndexes.length,
              })}
            </div>
            {!this.props.hideTransfer && (
              <React.Fragment>
                <Button
                  size="small"
                  className="mr-3"
                  onClick={this.handleTransfer(this.selectedNotifications)}
                >
                  {this.props.t("TRANSFER")}
                </Button>
                <Button
                  size="small"
                  className="mr-3"
                  onClick={this.handleCopy(this.selectedNotifications)}
                >
                  {this.props.t("COPY")}
                </Button>
              </React.Fragment>
            )}
            <Button color="negative" size="small" onClick={this.showDeleteModal()}>
              {this.props.t("DELETE_SELECTED")}
            </Button>
            <Button color="secondary" size="small" onClick={this.deselectAll}>
              {this.props.t("DESELECT_ALL")}
            </Button>
          </div>
        )}
        {this.props.pagination && (
          <Pagination
            className="mx-auto mt-3 d-block"
            currentPage={this.props.pagination.pageNumber}
            totalPages={this.props.pagination.totalPages}
            onChange={this.handlePaginationChange}
          />
        )}
        {this.state.isDeleteModalOpen && (
          <NotificationDeleteModal
            labels={this.notificationEmployeesLabels}
            onDismissHandler={this.closeDeleteModal}
            onSubmit={this.handleDelete}
            modalTitle={this.props.deleteModalTitle}
            modalContent={this.props.deleteModalContent}
            modalContentPlural={this.props.deleteModalContentPlural}
          />
        )}
        {this.state.actionModal.isOpen && (
          <NotificationActionModal
            actionType={actionType}
            data={this.state.actionModal.data}
            clientId={this.props.clientId}
            onSubmit={this.showConfirmActionModal}
            onDismissHandler={this.handleCloseActionModal}
            employeeRefType={this.props.employeeRefType}
            employeeName={this.props.employeeName}
          />
        )}

        {this.state.showConfirmActionModal && (
          <ConfirmationModal
            headerText={this.props.t(
              actionType === "transfer" ? "TRANSFER_NOTIFICATION" : "COPY_NOTIFICATION",
            )}
            bodyContent={this.props.t("ARE_YOU_SURE_ACTION_USER", {
              action: actionType,
              user: this.state.actionModal.data[0].recipient.name,
            })}
            confirmBtnLabel={this.props.t(actionType.toUpperCase())}
            confirmBtnIcon="replay_circle_filled"
            onCancel={this.discardActionModal}
            onConfirm={this.handleConfirmSubmitAction}
          />
        )}

        {this.props.individualsRevisionHistoryModal.visible && (
          <ConfirmationModal
            modalSize="normal"
            onCancel={this.toggleIndividualsRevisionHistoryModal}
            headerText={this.props.t("NOTIFICATION_CONTACT_HISTORY")}
            bodyContent={
              <GSGrid
                columns={[
                  {
                    key: "notificationType",
                    name: this.props.t("NOTIFICATION_TYPE"),
                    renderCell: ({ row }: Object) => {
                      return this.props.t(row.notificationType);
                    },
                  },
                  {
                    key: "notificationScopes",
                    name: this.props.t("NOTIFICATION_SCOPES"),
                    renderCell: ({ row }: Object) => {
                      return !row.notificationScopes || row.notificationScopes.length === 0
                        ? this.props.t("ALL_POSITIONS")
                        : row.notificationScopes.join(", ");
                    },
                  },
                  {
                    key: "updatedOn",
                    name: this.props.t("DATE_UPDATED"),
                    renderCell: ({ row }: Object) => {
                      return formatDateTime(row.updatedOn);
                    },
                    width: "min-content",
                  },
                  {
                    key: "updatedBy",
                    name: this.props.t("UPDATED_BY"),
                    renderCell: ({ row }: Object) =>
                      GSRenderers.updatedBy({
                        updatedBy: { ...row.updatedByObject },
                        t: this.props.t,
                      }),
                    width: "min-content",
                  },
                ]}
                rows={this.revisionHistoryData}
              />
            }
          />
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: State, ownProps: OwnProps): PropsFromState => {
  return {
    individualsRevisionHistoryModal: getIndividualsRevisionHistoryModal(state),
    notifications: getNotificationsIndividuals(state, refType(ownProps.employeeRefType)),
    pagination: getNotificationsIndividualsPagination(state, refType(ownProps.employeeRefType)),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getNotifications: actionCreators.GetNotifications,
  getMyEmployeeNotifications: actionCreators.GetMyEmployeeNotifications,
  getNotificationsTargets: actionCreators.GetNotificationsTargets,
  deleteNotificationList: actionCreators.DeleteNotificationList,
  deleteNotificationTargetList: actionCreators.DeleteNotificationTargetList,
  getNotificationEmployee: actionCreators.GetNotificationEmployee,
  transferNotificationList: actionCreators.TransferNotificationList,
  transferNotificationTargetList: actionCreators.TransferNotificationTargetList,
  copyNotificationList: actionCreators.CopyNotificationList,
  copyNotificationTargetList: actionCreators.CopyNotificationTargetList,
  deleteManagerNotificationList: actionCreators.DeleteManagerNotificationList,
  toggleIndividualsRevisionHistoryModal: actionCreators.ToggleIndividualsRevisionHistoryModal,
  getIndividualsRevisionHistory: actionCreators.GetIndividualsRevisionHistory,
  resetNotificationList: actionCreators.ResetNotificationModalEmployeeList,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(NotificationsIndividuals);
